import { ChevronLeft, ChevronRight, Lock, RotateCw } from "lucide-react";
import { Img, RedactOverlay, useRedacted } from "@/components/common";
import { cn } from "@/lib/utils";

export const Screen = ({ src, alt, ratio = "16/10", position = "top", className, sizes }) => {
  const redacted = useRedacted();
  return (
    <div className={cn("relative overflow-hidden bg-black", className)} style={{ aspectRatio: ratio }}>
      <Img src={src} alt={alt} sizes={sizes} className="absolute inset-0 h-full w-full object-cover" style={{ objectPosition: position }} />
      {redacted && <RedactOverlay compact />}
    </div>
  );
};

export const BrowserFrame = ({ src, alt, url = "lutazzi.studio" }) => (
  <div className="frame-shadow overflow-hidden rounded-xl border border-line/15 bg-elev" data-testid="frame-browser">
    <div className="flex items-center gap-3 border-b border-line/10 bg-surface px-3 py-2.5 sm:px-4">
      <div className="flex gap-1.5" aria-hidden>
        <span className="h-2.5 w-2.5 rounded-full bg-[#ff5f57]" />
        <span className="h-2.5 w-2.5 rounded-full bg-[#febc2e]" />
        <span className="h-2.5 w-2.5 rounded-full bg-[#28c840]" />
      </div>
      <div className="hidden items-center gap-2 text-silver sm:flex" aria-hidden>
        <ChevronLeft className="h-4 w-4" />
        <ChevronRight className="h-4 w-4 opacity-50" />
        <RotateCw className="h-3.5 w-3.5" />
      </div>
      <div className="mx-auto flex min-w-0 max-w-md flex-1 items-center justify-center gap-2 rounded-md bg-canvas/70 px-3 py-1 font-mono text-[10px] text-silver sm:text-[11px]">
        <Lock className="h-2.5 w-2.5 shrink-0 text-turq" aria-hidden />
        <span className="truncate">{url}</span>
      </div>
      <div className="hidden w-16 sm:block" />
    </div>
    <Screen src={src} alt={alt} ratio="3/2" />
  </div>
);

export const MacDesktop = ({ src, alt }) => (
  <div className="w-full" data-testid="frame-mac-desktop">
    <div className="frame-shadow rounded-[clamp(10px,1.6vw,22px)] bg-gradient-to-b from-[#e4e7e9] to-[#b4babe] p-[1.1%]">
      <div className="rounded-[clamp(7px,1.1vw,15px)] bg-[#050505] p-[1.8%]">
        <Screen src={src} alt={alt} ratio="16/9" />
      </div>
      <div className="flex h-[clamp(16px,3.4vw,48px)] items-center justify-center" aria-hidden>
        <span className="h-[clamp(4px,0.6vw,8px)] w-[clamp(4px,0.6vw,8px)] rounded-full bg-black/15" />
      </div>
    </div>
    <div
      className="mx-auto h-[clamp(26px,6vw,96px)] w-[17%] bg-gradient-to-b from-[#a4aaae] via-[#cfd3d6] to-[#aeb4b8]"
      style={{ clipPath: "polygon(14% 0, 86% 0, 100% 100%, 0 100%)" }}
      aria-hidden
    />
    <div className="mx-auto h-[clamp(4px,0.7vw,9px)] w-[26%] rounded-b-md bg-gradient-to-b from-[#c4c9cc] to-[#8f9599]" aria-hidden />
  </div>
);

export const Laptop = ({ src, alt }) => (
  <div className="w-full" data-testid="frame-laptop">
    <div className="frame-shadow relative mx-auto w-[86%] rounded-t-[clamp(8px,1.4vw,18px)] border border-[#2c3033] bg-[#0a0b0c] p-[1.5%] pt-[2.4%]">
      <span className="absolute left-1/2 top-[0.8%] h-[clamp(3px,0.45vw,6px)] w-[clamp(3px,0.45vw,6px)] -translate-x-1/2 rounded-full bg-[#1f3237]" aria-hidden />
      <Screen src={src} alt={alt} ratio="16/10" />
    </div>
    <div className="relative mx-auto h-[clamp(9px,1.5vw,20px)] w-full rounded-b-[clamp(8px,1.6vw,22px)] bg-gradient-to-b from-[#eceef0] via-[#c6cbce] to-[#878d91]" aria-hidden>
      <span className="absolute left-1/2 top-0 h-[38%] w-[13%] -translate-x-1/2 rounded-b-lg bg-[#9ea4a8]" />
    </div>
  </div>
);

export const IPhone = ({ src, alt, className }) => (
  <div className={cn("relative mx-auto w-full max-w-[300px]", className)} data-testid="frame-iphone">
    <span className="absolute -left-[1.5%] top-[18%] h-[5%] w-[1.6%] rounded-l bg-[#a9afb3]" aria-hidden />
    <span className="absolute -left-[1.5%] top-[26%] h-[8%] w-[1.6%] rounded-l bg-[#a9afb3]" aria-hidden />
    <span className="absolute -right-[1.5%] top-[24%] h-[11%] w-[1.6%] rounded-r bg-[#a9afb3]" aria-hidden />
    <div className="frame-shadow rounded-[17%/8%] bg-gradient-to-br from-[#e6e9eb] via-[#a4aaae] to-[#d3d7da] p-[2.2%]">
      <div className="relative rounded-[15.5%/7.3%] bg-black p-[3.2%]">
        <div className="overflow-hidden rounded-[12.5%/5.9%]">
          <Screen src={src} alt={alt} ratio="9/18" position="center" sizes="300px" />
        </div>
        <span className="absolute left-1/2 top-[3%] z-20 h-[3.4%] w-[31%] -translate-x-1/2 rounded-full bg-black" aria-hidden />
      </div>
    </div>
  </div>
);

export const Android = ({ src, alt, className }) => (
  <div className={cn("relative mx-auto w-full max-w-[300px]", className)} data-testid="frame-android">
    <span className="absolute -right-[1.4%] top-[20%] h-[10%] w-[1.4%] rounded-r bg-[#5d6366]" aria-hidden />
    <div className="frame-shadow rounded-[11%/5.5%] border border-[#6f767a] bg-[#16181a] p-[2.6%]">
      <div className="relative overflow-hidden rounded-[9%/4.4%]">
        <Screen src={src} alt={alt} ratio="9/18" position="center" sizes="300px" />
        <span className="absolute left-1/2 top-[2%] z-20 h-[3.4%] w-[7%] -translate-x-1/2 rounded-full bg-black ring-1 ring-white/10" aria-hidden />
      </div>
    </div>
  </div>
);

export const Tablet = ({ src, alt, landscape }) => (
  <div className={cn("mx-auto w-full", landscape ? "max-w-[900px]" : "max-w-[520px]")} data-testid="frame-tablet">
    <div className="frame-shadow relative rounded-[clamp(14px,3vw,36px)] bg-[#0d0e0f] p-[3.2%] ring-2 ring-[#a9afb3]/70">
      <span className={cn("absolute h-1.5 w-1.5 rounded-full bg-[#22343a]", landscape ? "left-[1.4%] top-1/2 -translate-y-1/2" : "left-1/2 top-[1.4%] -translate-x-1/2")} aria-hidden />
      <div className="overflow-hidden rounded-[clamp(6px,1vw,12px)]">
        <Screen src={src} alt={alt} ratio={landscape ? "4/3" : "3/4"} />
      </div>
    </div>
  </div>
);

export const FullscreenUI = ({ src, alt }) => (
  <div className="relative" data-testid="frame-fullscreen-ui">
    <div className="absolute -inset-x-6 -bottom-10 top-10 rounded-full bg-turq/15 blur-3xl" aria-hidden />
    <div className="relative overflow-hidden rounded-lg shadow-[0_60px_120px_-40px_rgba(0,0,0,0.75)] ring-1 ring-line/15">
      <Screen src={src} alt={alt} ratio="3/2" />
    </div>
  </div>
);

export const ResponsiveSet = ({ images, alt }) => (
  <div className="relative pb-[4%]" data-testid="frame-responsive-set">
    <div className="w-[80%]">
      <MacDesktop src={images[0]} alt={`${alt} — desktop`} />
    </div>
    <div className="absolute bottom-0 right-0 w-[27%]">
      <Tablet src={images[1]} alt={`${alt} — tablet`} />
    </div>
    <div className="absolute bottom-0 right-[29%] w-[13%]">
      <IPhone src={images[2]} alt={`${alt} — mobile`} />
    </div>
  </div>
);
